import { useState, useEffect } from "react";
import { Link } from "wouter";
import { ChevronDown, Brain, Check, Lock, MinusCircle } from "lucide-react";
import type { ProjectDetail, OrgContext, ClientContext, Strategy } from "@/lib/api";

type CheckpointState = "none" | "partial" | "strong";

interface AiBrainPanelProps {
  project?: ProjectDetail | null;
  projectId?: string;
  orgContext?: OrgContext | null;
  clientContext?: ClientContext | null;
  strategy?: Strategy | null;
}

export default function AiBrainPanel({
  project,
  projectId,
  orgContext,
  clientContext,
  strategy,
}: AiBrainPanelProps) {
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("cowork-rfp-brain-collapsed");
    if (stored === "false") setCollapsed(false);
  }, []);

  const handleToggle = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem("cowork-rfp-brain-collapsed", String(newState));
  };

  // ---------------------------------------------------------------------------
  // Each checkpoint is scored none / partial / strong based on substance, not
  // just non-empty. Thresholds are deliberately on the high side so users only
  // get "strong" when there's enough text to actually feed a good prompt.
  // ---------------------------------------------------------------------------

  const summaryState: CheckpointState = project?.summary ? "strong" : "none";

  const agencyState: CheckpointState = (() => {
    if (!orgContext) return "none";
    const desc = orgContext.agencyDescription.length;
    const pos = orgContext.positioning.length;
    const diff = orgContext.differentiators.length;
    // Strong: a real description (200+ chars) PLUS at least one of positioning
    // or differentiators with substance.
    if (desc >= 200 && (pos >= 30 || diff >= 50)) return "strong";
    // Partial: any meaningful content in any field.
    if (desc >= 40 || pos >= 30 || diff >= 50) return "partial";
    return "none";
  })();

  const voiceState: CheckpointState = (() => {
    if (!orgContext) return "none";
    const tone = orgContext.toneOfVoice !== null;
    const focus = orgContext.focusAreas.length;
    if (tone && focus >= 3) return "strong";
    if (tone || focus >= 1) return "partial";
    return "none";
  })();

  const clientState: CheckpointState = (() => {
    if (!clientContext) return "none";
    const about = clientContext.aboutClient.length;
    const market = clientContext.theirMarket.length;
    const competitors = clientContext.competitors.length;
    const notes = clientContext.additionalNotes.length;
    if (about >= 150 && (market >= 50 || competitors >= 30 || notes >= 50)) return "strong";
    if (about >= 40 || market >= 30 || competitors >= 20 || notes >= 30) return "partial";
    return "none";
  })();

  const strategyState: CheckpointState = (() => {
    if (!strategy) return "none";
    const positioning = strategy.positioningStatement.length;
    const themes = strategy.winThemes.length;
    if (positioning >= 100 && themes >= 2) return "strong";
    if (positioning >= 30 || themes >= 1) return "partial";
    return "none";
  })();

  const checkpoints: { label: string; link: string | null; state: CheckpointState }[] = [
    { label: "Read the RFP scope", link: null, state: summaryState },
    {
      label: "Knows your agency",
      link: projectId ? `/projects/${projectId}/about-us` : null,
      state: agencyState,
    },
    {
      label: "Knows this client",
      link: projectId ? `/projects/${projectId}/about-client` : null,
      state: clientState,
    },
    {
      label: "Has a strategy",
      link: projectId ? `/projects/${projectId}/strategy` : null,
      state: strategyState,
    },
    {
      label: "Knows your voice",
      link: projectId ? `/projects/${projectId}/about-us` : null,
      state: voiceState,
    },
  ];

  // Score: strong=2, partial=1, none=0 → max 10.
  const score = checkpoints.reduce(
    (acc, c) => acc + (c.state === "strong" ? 2 : c.state === "partial" ? 1 : 0),
    0,
  );

  const getLabel = (): string => {
    if (score <= 1) return "Just started";
    if (score <= 3) return "Getting briefed";
    if (score <= 5) return "Halfway there";
    if (score <= 8) return "Well briefed";
    return "Fully briefed";
  };

  if (collapsed) {
    return (
      <button
        onClick={handleToggle}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-white transition-all"
        style={{ backgroundColor: "rgba(249,115,22,0.15)" }}
        title="Click to expand AI Confidence panel"
      >
        <Brain size={14} />
      </button>
    );
  }

  return (
    <div
      className="rounded-lg p-4 border"
      style={{
        backgroundColor: "rgba(249,115,22,0.05)",
        borderColor: "rgba(249,115,22,0.2)",
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Brain size={16} style={{ color: "#F97316" }} />
          <span className="text-sm font-semibold text-white">
            AI Confidence: <span style={{ color: "#F97316" }}>{getLabel()}</span> —{" "}
            <span style={{ color: "#9CA3AF" }}>{score} of 10 points</span>
          </span>
        </div>
        <button
          onClick={handleToggle}
          className="text-gray-400 hover:text-white transition-colors"
          title="Click to collapse"
        >
          <ChevronDown size={14} />
        </button>
      </div>

      <div className="space-y-2">
        {checkpoints.map((checkpoint, idx) => (
          <CheckpointRow key={idx} checkpoint={checkpoint} />
        ))}
      </div>

      <p className="text-[10px] mt-4 leading-relaxed" style={{ color: "#6B7280" }}>
        Strong = enough detail for the AI to draft from. Partial = something there but thin — adding
        more will sharpen the output.
      </p>
    </div>
  );
}

function CheckpointRow({
  checkpoint,
}: {
  checkpoint: { label: string; link: string | null; state: CheckpointState };
}) {
  const { label, link, state } = checkpoint;

  const icon =
    state === "strong" ? (
      <Check size={14} style={{ color: "#10B981" }} />
    ) : state === "partial" ? (
      <MinusCircle size={14} style={{ color: "#EAB308" }} />
    ) : (
      <Lock size={14} style={{ color: "#6B7280" }} />
    );

  const textColor = state === "strong" ? "#D1D5DB" : state === "partial" ? "#EAB308" : "#9CA3AF";

  const labelText =
    state === "strong" ? label : state === "partial" ? `${label} — add more` : label;

  return (
    <div className="flex items-start gap-3">
      <div className="pt-0.5">{icon}</div>
      {link && state !== "strong" ? (
        <Link href={link}>
          <button
            className="text-sm font-medium transition-colors text-left"
            style={{ color: state === "partial" ? "#EAB308" : "#F97316" }}
          >
            {labelText}
          </button>
        </Link>
      ) : (
        <span className="text-sm font-medium" style={{ color: textColor }}>
          {labelText}
        </span>
      )}
    </div>
  );
}
